import { Paper, Typography, makeStyles } from '@material-ui/core'
import useTranslate from '../../../../../i18n/useTranslate'
import { ItemListType } from '../../../../../services/web/types/responseWebServiceTypes'
import { statusColorConstant } from '../../../../../constants/constants'
import projectStatus from '../../../../../constants/projectStatus'
import pendingStatus from '../../../../../constants/pendingStatus'
import submittedStatus from '../../../../../constants/submittedStatus'

type Props = {
    data: ItemListType
    isResearcher: boolean
    isDot?: boolean
}

type ColorEntry = {
    statusID: number
    backgroundColor: string
    pendingBackgroundColor?: string
    textColor?: string
    submittedStatusColor?: string
}

const DEFAULT_BG = '#f2f2f5'
const DEFAULT_TEXT = '#111'
const DEFAULT_SUBMITTED = '#999'

const colorByStatusId = Object.values(statusColorConstant as any)
    .filter((v: any): v is ColorEntry => v && typeof v.statusID === 'number')
    .reduce<Record<number, ColorEntry>>((acc, e) => {
        acc[e.statusID] = e
        return acc
    }, {})

const ProjectBg: Record<number, string | undefined> = {
    [projectStatus.rejected]: statusColorConstant.statusReject?.backgroundColor,
    [projectStatus.closed]: statusColorConstant.statusClose?.backgroundColor,
    [projectStatus.notScope]: statusColorConstant.noScope?.backgroundColor,
    [projectStatus.terminate]: statusColorConstant.terminate?.backgroundColor,
    [projectStatus.withdraw]: statusColorConstant.withdraw?.backgroundColor,
    [projectStatus.completed]: statusColorConstant.status8?.backgroundColor,
}

function getProcessingBg(row: ItemListType) {
    const s15 = (statusColorConstant as any).status15 as ColorEntry | undefined
    if (!s15) return undefined
    return row.pendingStatusID === pendingStatus.pending
        ? (s15.pendingBackgroundColor ?? s15.backgroundColor)
        : s15.backgroundColor
}

function getStatusPalette(row: ItemListType) {
    if (!row.isSubmittedReceiveDate && row.isDraft) {
        const s1 = (statusColorConstant as any).status1 as ColorEntry | undefined
        return {
            projectBg: s1?.backgroundColor ?? DEFAULT_BG,
            textColor: s1?.textColor ?? DEFAULT_TEXT,
            submittedColor: s1?.submittedStatusColor ?? s1?.backgroundColor ?? DEFAULT_SUBMITTED,
        }
    }

    const submittedEntry = row.submittedStatusID != null ? colorByStatusId[row.submittedStatusID] : undefined

    let projectBg: string | undefined
    if (row.projectStatusID === projectStatus.processing) {
        projectBg = getProcessingBg(row)
    } else {
        projectBg = ProjectBg[row.projectStatusID]
    }

    projectBg = projectBg ?? submittedEntry?.backgroundColor ?? DEFAULT_BG
    const textColor = submittedEntry?.textColor ?? DEFAULT_TEXT
    const submittedColor = submittedEntry?.submittedStatusColor ?? submittedEntry?.backgroundColor ?? DEFAULT_SUBMITTED

    return { projectBg, textColor, submittedColor }
}

function getProjectStatusText({
    data,
    isResearcher,
    isTH,
    translate,
}: {
    data: ItemListType
    isResearcher: boolean
    isTH: boolean
    translate: ReturnType<typeof useTranslate>
}) {
    if (data.projectStatusID === projectStatus.processing && data.pendingStatusID === pendingStatus.pending) {
        return translate.get('LABEL.PENDING')
    }
    if (isResearcher && data.submittedStatusID === submittedStatus.waitingEditProject) {
        return translate.get('LABEL.RECEIVED_REVIEW_RESULT')
    }
    return isTH ? (data.projectStatus_th ?? '-') : (data.projectStatus_en ?? '-')
}

const ProjectStatusBadge = ({ data, isResearcher, isDot }: Props) => {
    const translate = useTranslate()
    const isTH = translate.languageStore.currentLanguage === translate.languageKey.th

    const palette = getStatusPalette(data)
    const classes = useStyles({
        bg: palette.projectBg,
        color: palette.textColor,
        borderColor: palette.projectBg,
    })

    const text = getProjectStatusText({ data, isResearcher, isTH, translate })

    if (isDot) {
        return <span className={classes.dot} />
    }

    return (
        <Paper variant="outlined" className={classes.root}>
            <Typography variant="body2" className={classes.text}>
                {text}
            </Typography>
        </Paper>
    )
}

const useStyles = makeStyles<any, { bg: string; color: string; borderColor: string }>(() => ({
    root: (p) => ({
        padding: '2px 10px',
        borderRadius: 8,
        border: `1px solid ${p.borderColor}`,
        background: p.bg,
        color: p.color,
        textAlign: 'center',
        display: 'inline-flex',
        alignItems: 'center',
        maxWidth: 320,
    }),
    text: {
        maxWidth: 280,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
    dot: (p) => ({
        display: 'inline-block',
        height: '15px',
        width: '15px',
        borderRadius: '50%',
        background: p.bg,
    }),
}))

export default ProjectStatusBadge
